import { createBrowserRouter } from "react-router-dom";
import React from "react";
import {
    AddEmployee,
    AddNewProductList,
    AddProductToTechMap,
    BookingStatistic,
    Branch,
    Clients,
    ClientsHistoryVisit,
    CreateBranch,
    CreateClient,
    CreateProduct,
    CreateService,
    CurrentBranch,
    EditingEmployee,
    EmployeesList,
    HistoryChangeAppointment,
    OnlineBookingSettings,
    OwnerCreation,
    PaymentHistory,
    PaymentHistoryAndManagement,
    Positions,
    ProductsForTechMap,
    ProductsList,
    RecoveryProduct,
    RecoveryService,
    Reports,
    Reviews,
    Salary,
    Services,
    TariffPlans,
    TechMapServices,
    TechMapsListForService,
    TechnologicalMapsList,
    UpdateClient,
    UpdateCompany,
    UpdateProduct,
    UpdateService,
    EmployeeServices,
    ServiceProviders,
    MainMenuBeautySalon,
    Logbook,
    WorkSchedule,
    BranchAccessSettings,
    Warehouse,
    WarehousesGoods,
    WarehousesOperations,
    OperationHistoryPage,
    AllMenusPage,
    MenuItemsPage,
    DishUpdatePage,
    DishCreatePage,
    SupplementsForDishesPage,
    CategoriesDishesPage,
    SalesStatistics,
    RestaurantBooking,
    RestaurantRequest,
    WarehousesModuleDetailsPage,
    MenuModuleDetailsPage,
} from "../components";
import CreateCompany from "../components/CreateCompany/CreateCompany";
import { AddRecord } from "../components/OnlineRegistration/AddRecord/AddRecord";
import Page500 from "./errorPages/Page500";
import { ServiceCategories } from "../components/Services/ServiceCategories/ServiceCategories";
import ProtectedRegistrationRoute from "../components/CreateCompany/ProtectedRegistrationRoute";
import ProtectedRoute from "./ProtectedRoute";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { AllModulesTagName } from "../utils/enums";
import Page403 from "./errorPages/Page403";
import { ProductCategories } from "../components/Products/ProductCategories/ProductCategories";
import { TechMapCategories } from "../components/TechnologicalMaps/TechMapCategories/TechMapCategories";
import Modules from "../components/TariffManagement/Modules/Modules";
import { WriteOffProduct } from "../components/Products/WriteOffProduct/WriteOffProduct";
import ProtectedRouteWithoutCompany from "./ProtectedRouteWithoutCompany";
import MenuEditContent from "../components/MenuEditContentPage/MenuEditContent";
import MenuViewPage from "../components/Me-menu/MenuViewPage/MenuViewPage";
import PublishedMenuPage from "../components/Me-menu/PublishedMenuPage/PublishedMenuPage";
import PaymentStatusPage from "../components/TariffManagement/PaymentStatusPage/PaymentStatusPage";
import WithDocumentTitle from "./withDocumentTitle";
import ProtectedOwnerRoute from "../components/OwnerCreation/ProtectedOwnerRoute";
import RoleManagementComponent from "../components/AccessSettings/RoleManagementComponent/RoleManagementComponent";
import RoleDetailsPage from "../components/AccessSettings/RoleManagementComponent/RoleDetailsPage/RoleDetailsPage";

const commonRoutes = [
    {
        path: "branches/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.BranchView}>
                {React.createElement(WithDocumentTitle(Branch, "Branches"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "create-branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.BranchCreate}>
                {React.createElement(WithDocumentTitle(CreateBranch, "Branch"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "edit-branch/:updateId/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.BranchView}>
                <CurrentBranch />
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "restore-branch/:updateId/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.BranchDeleteRestore}>
                <CurrentBranch />
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/access-settings/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.AccessSettingsView}>
                <BranchAccessSettings />
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "clients-history-visit/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.ClientListView}
                requiredModules={[AllModulesTagName.VISIT_HISTORY]}
            >
                {React.createElement(WithDocumentTitle(ClientsHistoryVisit, "History of client visits"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "service-categories/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.ServiceCategoryListView}
                requiredModules={[AllModulesTagName.SERVICES, AllModulesTagName.SERVICE_CATEGORIES]}
            >
                {React.createElement(WithDocumentTitle(ServiceCategories, "Service Categories"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "/registration",
        element: <ProtectedRegistrationRoute element={WithDocumentTitle(CreateCompany, "Create company")} />,
    },
    {
        path: "employees-list/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.EmployeeListView}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EmployeesList, "List of employees"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "employees-list/:position/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.EmployeeListView}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EmployeesList, "List of employees"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "employees-list/employee/:userId/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.EmployeeListView}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EmployeesList, "List of employees"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "employees-list/add-employee/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.EmployeeCreate}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(AddEmployee, "Add an employee"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "employees-list/edit-employee/:id/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[CompanyAccess.EmployeeView, CompanyAccess.EmployeeEdit]}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EditingEmployee, "Edit"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "create-owner/:branchId",
        element: (
            <ProtectedOwnerRoute>
                {React.createElement(WithDocumentTitle(OwnerCreation, "Add owner"))}
            </ProtectedOwnerRoute>
        ),
    },
    {
        path: "positions/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.PositionListView}
                requiredModules={[AllModulesTagName.POSITIONS]}
            >
                {React.createElement(WithDocumentTitle(Positions, "Positions"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "/logbook/edit-record/:bookingId/change-history/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.LogbookHistoryView}
                requiredModules={[AllModulesTagName.LOGBOOK]}
            >
                {React.createElement(WithDocumentTitle(HistoryChangeAppointment, "History of Changes"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "error/500",
        element: React.createElement(WithDocumentTitle(Page500, "Server error")),
    },
    {
        path: "error/403",
        element: React.createElement(WithDocumentTitle(Page403, "Access denied")),
    },
    {
        path: "services/service/:currentCompanyId/service-list/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ServiceListView} requiredModules={[AllModulesTagName.SERVICES]}>
                {React.createElement(WithDocumentTitle(Services, "Service list"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "service-categories/:branchId/:currentCompanyId/service-list/categoryId/:categoryId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ServiceListView} requiredModules={[AllModulesTagName.SERVICES]}>
                {React.createElement(WithDocumentTitle(Services, "Service list"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "service-categories/:branchId/:currentCompanyId/service-list/subCategoryId/:subCategoryId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ServiceListView} requiredModules={[AllModulesTagName.SERVICES]}>
                {React.createElement(WithDocumentTitle(Services, "Service list"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "services/service/:currentCompanyId/new/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ServiceCreate} requiredModules={[AllModulesTagName.SERVICES]}>
                {React.createElement(WithDocumentTitle(CreateService, "Add Service"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "services/service/:currentCompanyId/service-list/:id/edit/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ServiceListView} requiredModules={[AllModulesTagName.SERVICES]}>
                {React.createElement(WithDocumentTitle(UpdateService, "Editing Service"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "services/service/:currentCompanyId/service-list/:id/restore/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.ServiceDeleteRestore}
                requiredModules={[AllModulesTagName.SERVICES]}
            >
                {React.createElement(WithDocumentTitle(RecoveryService, "Recovery Service"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "clients/:currentCompanyId/list/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ClientListView} requiredModules={[AllModulesTagName.CLIENTS]}>
                {React.createElement(WithDocumentTitle(Clients, "Client list"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "clients/:currentCompanyId/list/:id/edit/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ClientListView} requiredModules={[AllModulesTagName.CLIENTS]}>
                {React.createElement(WithDocumentTitle(UpdateClient, "Editing"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "clients/:currentCompanyId/new/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ClientCreate} requiredModules={[AllModulesTagName.CLIENTS]}>
                {React.createElement(WithDocumentTitle(CreateClient, "New Client"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "booking/review/company/:currentCompanyId/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ReviewListView} requiredModules={[AllModulesTagName.REVIEWS]}>
                {React.createElement(WithDocumentTitle(Reviews, "List of reviews"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "booking/review/company/:currentCompanyId/:userId/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ReviewListView} requiredModules={[AllModulesTagName.REVIEWS]}>
                {React.createElement(WithDocumentTitle(Reviews, "List of reviews"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "company/:currentCompanyId/edit/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.CompanyInfoView}>
                {React.createElement(WithDocumentTitle(UpdateCompany, "Company"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "personnel/:currentCompanyId/salary/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.SalaryView} requiredModules={[AllModulesTagName.SALARY]}>
                {React.createElement(WithDocumentTitle(Salary, "Salary"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "personnel/:currentCompanyId/payment-history/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.PaymentsHistoryView} requiredModules={[AllModulesTagName.SALARY]}>
                {React.createElement(WithDocumentTitle(PaymentHistory, "History of Payments"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "payment/company/:currentCompanyId/tariff/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TariffView}>
                {React.createElement(WithDocumentTitle(TariffPlans, "Tariff Plans"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "payment/history/company/:currentCompanyId/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TariffView}>
                {React.createElement(WithDocumentTitle(PaymentHistoryAndManagement, "History and Payment Management"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "payment/company/:currentCompanyId/after-payment/order/:orderId/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TariffView}>
                {React.createElement(WithDocumentTitle(PaymentStatusPage, "Payment Status Page"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "booking/company/:currentCompanyId/config/:branchId",
        element: (
            <ProtectedRoute
                anyPermissions={[CompanyAccess.OnlineBookingSettingsView, CompanyAccess.OnlineBookingSettingsEdit]}
                requiredModules={[AllModulesTagName.BOOKING]}
            >
                {React.createElement(WithDocumentTitle(OnlineBookingSettings, "Online Booking Settings"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "logbook/add-record/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.LogbookBookingCreate}
                requiredModules={[AllModulesTagName.LOGBOOK]}
            >
                {React.createElement(WithDocumentTitle(AddRecord, "Add new booking"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: `logbook/edit-record/:bookingUuid/:branchId`,
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[CompanyAccess.LogbookBookingEdit, CompanyAccess.LogbookView]}
                requiredModules={[AllModulesTagName.LOGBOOK]}
            >
                {React.createElement(WithDocumentTitle(AddRecord, "Edit booking"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "employees-list/employee-services/:id/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.EmployeeServicesView}
                requiredModules={[AllModulesTagName.PERSONNEL, AllModulesTagName.SERVICES]}
            >
                {React.createElement(WithDocumentTitle(EmployeeServices, "Employee's services"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "services-providers/:id/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.ServiceEmployeesView}
                requiredModules={[AllModulesTagName.PERSONNEL, AllModulesTagName.SERVICES]}
            >
                {React.createElement(WithDocumentTitle(ServiceProviders, "Service Providers"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branches/main/branch/:branchId",
        element: React.createElement(WithDocumentTitle(MainMenuBeautySalon, "Branches")),
    },
    {
        path: "logbook/journal/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.LogbookView}
                requiredModules={[AllModulesTagName.LOGBOOK]}
            >
                {React.createElement(WithDocumentTitle(Logbook, "Logbook Journal"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "work-schedule/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.ScheduleCalendarView}
                requiredModules={[AllModulesTagName.WORK_SCHEDULE]}
            >
                {React.createElement(WithDocumentTitle(WorkSchedule, "Staff Work Schedule"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "work-schedule/:id/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.ScheduleCalendarView}
                requiredModules={[AllModulesTagName.WORK_SCHEDULE]}
            >
                {React.createElement(WithDocumentTitle(WorkSchedule, "Staff Work Schedule"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "/:ln/personnel/employee/:companyId/list/:branchId/:id/edit",
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[CompanyAccess.EmployeeView, CompanyAccess.EmployeeEdit]}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EditingEmployee, "Edit"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "/personnel/employee/:companyId/list/:branchId/:id/edit",
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[CompanyAccess.EmployeeView, CompanyAccess.EmployeeEdit]}
                requiredModules={[AllModulesTagName.EMPLOYEES]}
            >
                {React.createElement(WithDocumentTitle(EditingEmployee, "Edit"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "logbook/statistic/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.BookingStatisticViewExport}
                requiredModules={[AllModulesTagName.STATISTIC]}
            >
                {React.createElement(WithDocumentTitle(BookingStatistic, "Online Booking Statistic"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouses/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(Warehouse, "Warehouse"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouse/product/:currentCompanyId/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ProductListView} requiredModules={[AllModulesTagName.WAREHOUSES]}>
                {React.createElement(WithDocumentTitle(ProductsList, "List of products"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/:currentCompanyId/new-product/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ProductCreate} requiredModules={[AllModulesTagName.WAREHOUSES]}>
                {React.createElement(WithDocumentTitle(CreateProduct, "Add New Product"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/product/:currentCompanyId/:id/edit-product/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.ProductEdit} requiredModules={[AllModulesTagName.WAREHOUSES]}>
                {React.createElement(WithDocumentTitle(UpdateProduct, "Edit product"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/product/:currentCompanyId/:id/restore/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.ProductDeleteRestore}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(RecoveryProduct, "Restore the Product"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/technological-maps/:currentCompanyId/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TechMapListView} requiredModules={[AllModulesTagName.WAREHOUSES]}>
                {React.createElement(WithDocumentTitle(TechnologicalMapsList, "List of Technological" + " Maps"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouses/warehouses-goods/branch/:branchId/:storageId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseProductListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(WarehousesGoods, "Warehouse"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouses/add-product/:id/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[CompanyAccess.WarehouseProductCreateAndAdd, CompanyAccess.WarehouseProductAdd]}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(AddNewProductList, "Add products to the warehouse"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouse/product-categories/:currentCompanyId/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.ProductCategoryListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(ProductCategories, "Product Categories"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/technological-map-categories/:currentCompanyId/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.TechMapCategoryListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(TechMapCategories, "Technological Map Categories"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/technological-maps/:currentCompanyId/:techMapId/services/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.TechMapServiceListView}
                requiredModules={[AllModulesTagName.WAREHOUSES, AllModulesTagName.SERVICES]}
            >
                {React.createElement(WithDocumentTitle(TechMapServices, "Add a Service to the Technological Map"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "services/service/:currentCompanyId/service-list/:serviceId/technological/maps/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.ServiceTechMapsView}
                requiredModules={[AllModulesTagName.SERVICES, AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(TechMapsListForService, "Technological maps for the service"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/technological-maps/:currentCompanyId/:techMapId/products/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.TechMapProductListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(ProductsForTechMap, "Products for the Technological Map"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/technological-maps/:currentCompanyId/:techMapId/product/add/branch/:branchId",
        element: (
            <ProtectedRoute
                permission={CompanyAccess.TechMapProductListEdit}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(AddProductToTechMap, "Add Product to the Technological Map"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "warehouse/reports/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseReportViewExport}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(Reports, "Reports"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouses/operations/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseOperationListView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(WarehousesOperations, "Warehouse Operations"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "payment/company/:currentCompanyId/modules/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TariffView}>
                {React.createElement(WithDocumentTitle(Modules, "Modules"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "products/write-off/company/:companyId/branch/:branchId/bookingUuid/:bookingUuid",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.LogbookConsumablesWriteOff}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(
                    WithDocumentTitle(() => <WriteOffProduct writeOffSource="logbook" />, "Write-off of Products")
                )}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "products/write-off/company/:companyId/branch/:branchId/bookingUuid/:bookingUuid/edit/operationNumber/:operationNumber",
        element: (
            <ProtectedRouteWithoutCompany
                anyPermissions={[
                    CompanyAccess.LogbookConsumablesWriteOff,
                    CompanyAccess.WarehouseOperationConsumablesWriteOffEdit,
                ]}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(
                    WithDocumentTitle(() => <WriteOffProduct writeOffSource="logbook" />, "Write-off of Products")
                )}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "products/write-off/warehouse-operation/company/:companyId/branch/:branchId/bookingUuid/:bookingUuid/edit/operationNumber/:operationNumber",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseOperationConsumablesWriteOffEdit}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(
                    WithDocumentTitle(
                        () => <WriteOffProduct writeOffSource="warehouseOperation" />,
                        "Write-off of Products"
                    )
                )}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouses/company/:companyId/branch/:branchId/warehouse-operations/:operationId/change-history",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.WarehouseOperationEditHistoryView}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(OperationHistoryPage, "History of Changes"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "warehouses/sales-statistics/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.BookingStatisticViewExport}
                requiredModules={[AllModulesTagName.WAREHOUSES]}
            >
                {React.createElement(WithDocumentTitle(SalesStatistics, "Sales statistics"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "me-booking/restaurant-booking",
        element: React.createElement(WithDocumentTitle(RestaurantBooking, "Restaurant Booking")),
    },
    {
        path: "me-booking/restaurant-booking/:branchId",
        element: React.createElement(WithDocumentTitle(RestaurantBooking, "Restaurant Booking")),
    },
    {
        path: "me-booking/restaurant-request",
        element: React.createElement(WithDocumentTitle(RestaurantRequest, "Restaurant Request")),
    },
    {
        path: "me-booking/restaurant-request/:branchId",
        element: React.createElement(WithDocumentTitle(RestaurantRequest, "Restaurant Request")),
    },
    {
        path: "payment/company/:currentCompanyId/modules/details/warehouses/branch/:branchId",
        element: (
            <ProtectedRoute permission={CompanyAccess.TariffView}>
                {React.createElement(WithDocumentTitle(WarehousesModuleDetailsPage, "Module warehouse accounting"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "menu/dishes/categories/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuDishCategoryListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(CategoriesDishesPage, "Category of dishes"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/page/:id/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(MenuEditContent, "menu"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/all-menus/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(AllMenusPage, "All menu"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/dishes/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuDishListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(MenuItemsPage, "List of dishes"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/update-dish/dish/:dishId/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuDishEdit}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(DishUpdatePage, "Edit dish"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/restore-dish/dish/:dishId/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuDishDeleteRestore}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(DishUpdatePage, "Restore dish"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/dishes/create-dish/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuDishCreate}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(DishCreatePage, "New dish"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/:id/preview/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(MenuViewPage, "Menu preview"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/:id/before-publish/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuCloneEdit}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(PublishedMenuPage, "Menu published"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "menu/dishes/add-ons/branch/:branchId",
        element: (
            <ProtectedRouteWithoutCompany
                permission={CompanyAccess.MenuOptionsAndSupplementsListView}
                requiredModules={[AllModulesTagName.MENU]}
            >
                {React.createElement(WithDocumentTitle(SupplementsForDishesPage, "Options and Add-ons for dishes"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "payment/company/:currentCompanyId/modules/details/menu/branch/:branchId",
        element: (
            <ProtectedRoute>
                {React.createElement(WithDocumentTitle(MenuModuleDetailsPage, "Module menu"))}
            </ProtectedRoute>
        ),
    },
    {
        path: "branch/role-management/:branchId",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.AccessSettingsView}>
                {React.createElement(WithDocumentTitle(RoleManagementComponent, "Role Management"))}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/role-management/:branchId/roles/create",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.RoleCreateClone}>
                {React.createElement(WithDocumentTitle(RoleDetailsPage, "Create role"), { mode: "create" })}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/role-management/:branchId/roles/:roleId/edit",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.RoleEdit}>
                {React.createElement(WithDocumentTitle(RoleDetailsPage, "Edit role"), { mode: "edit" })}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/role-management/:branchId/roles/:roleId/clone",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.RoleCreateClone}>
                {React.createElement(WithDocumentTitle(RoleDetailsPage, "Clone role"), { mode: "clone" })}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/role-management/:branchId/roles/:roleId/restore",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.RoleDelete}>
                {React.createElement(WithDocumentTitle(RoleDetailsPage, "Restore role"), { mode: "restore" })}
            </ProtectedRouteWithoutCompany>
        ),
    },
    {
        path: "branch/role-management/:branchId/roles/:roleId/view",
        element: (
            <ProtectedRouteWithoutCompany permission={CompanyAccess.AccessSettingsView}>
                {React.createElement(WithDocumentTitle(RoleDetailsPage, "View role"), { mode: "view" })}
            </ProtectedRouteWithoutCompany>
        ),
    },
];

export const router = createBrowserRouter(commonRoutes);

export default commonRoutes;
